/**
 * URL allowlist for clickable links inside Panther AI markdown responses.
 *
 * AI-generated content is untrusted — the model can hallucinate hostnames or
 * be coerced (via prompt injection) into emitting links to attacker-controlled
 * domains. To reduce the blast radius of a confused or compromised model, we
 * only make links clickable when they target a known vendor surface.
 *
 * Matching rules (intentionally strict):
 *   1. Must parse via `new URL()`.
 *   2. Scheme must be `https:` (blocks `javascript:`, `data:`, `file:`, `http:` …).
 *   3. Hostname (lowercased, credentials-stripped by URL parsing) must match an
 *      entry below — either an exact host match, or a subdomain match when
 *      `allowSubdomains` is true.
 *
 * We match full host labels, not suffixes: e.g. `evil.com/slack.com` has
 * hostname `evil.com`, and `slack.com.evil.com` does not end with `.slack.com`,
 * so both are correctly rejected.
 */

interface AllowedHost {
  host: string;
  allowSubdomains: boolean;
}

const ALLOWED_HOSTS: ReadonlyArray<AllowedHost> = [
  { host: 'docs.panther.com', allowSubdomains: false },
  { host: 'help.panther.com', allowSubdomains: false },
  { host: 'panther-knowledge-base.help.usepylon.com', allowSubdomains: false },
  { host: 'slack.com', allowSubdomains: true },
  { host: 'atlassian.net', allowSubdomains: true },
  { host: 'notion.so', allowSubdomains: true },
  { host: 'notion.site', allowSubdomains: true },
  { host: 'github.com', allowSubdomains: true },
];

export function isAllowedAILink(href: string | undefined | null): boolean {
  if (!href) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }

  if (url.protocol !== 'https:') {
    return false;
  }

  const hostname = url.hostname.toLowerCase();
  return ALLOWED_HOSTS.some(({ host, allowSubdomains }) => {
    if (hostname === host) {
      return true;
    }
    if (allowSubdomains && hostname.endsWith(`.${host}`)) {
      return true;
    }
    return false;
  });
}
